/**
 * Help Videos hub — browse explainer videos by site area.
 * Areas are defined in config/helpVideos.js so new modules can be added later.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import HelpVideoEmbed from '../components/HelpVideoEmbed';
import { usePermissions } from '../hooks/usePermissions';
import { HELP_VIDEO_AREAS, getHelpVideoArea, hasPlayableVideo } from '../config/helpVideos';
import './HelpVideosPage.css';

const HelpVideosPage = () => {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const areaParam = searchParams.get('area') || '';
  const videoParam = searchParams.get('video') || '';

  const visibleAreas = useMemo(
    () =>
      HELP_VIDEO_AREAS.filter(
        (area) => area.permission === null || hasPermission(area.permission)
      ),
    [hasPermission]
  );

  const selectedArea =
    getHelpVideoArea(areaParam) &&
    visibleAreas.some((a) => a.id === areaParam)
      ? getHelpVideoArea(areaParam)
      : null;

  const [openVideoId, setOpenVideoId] = useState(videoParam || null);

  useEffect(() => {
    if (videoParam) setOpenVideoId(videoParam);
  }, [videoParam]);

  const selectArea = (areaId) => {
    setSearchParams(areaId ? { area: areaId } : {});
    setOpenVideoId(null);
  };

  const toggleVideo = (videoId) => {
    const next = openVideoId === videoId ? null : videoId;
    setOpenVideoId(next);
    if (selectedArea) {
      const nextParams = { area: selectedArea.id };
      if (next) nextParams.video = next;
      setSearchParams(nextParams);
    }
  };

  return (
    <div className="entry-page help-videos-page">
      <PageHeader title="Help Videos" section="leaders">
        <p className="help-videos-intro">
          Short explainers for entering and managing ministry data. Pick a topic below—more
          areas will be added over time.
        </p>
      </PageHeader>

      {!selectedArea ? (
        <section className="help-videos-hub" aria-labelledby="help-videos-hub-heading">
          <h2 id="help-videos-hub-heading" className="help-videos-section-title">
            Topics
          </h2>
          {visibleAreas.length === 0 ? (
            <p className="help-videos-empty">
              No help topics are available for your account yet. Contact your administrator if you
              need access.
            </p>
          ) : (
            <ul className="help-videos-area-list">
              {visibleAreas.map((area) => {
                const readyCount = area.videos.filter(hasPlayableVideo).length;
                return (
                  <li key={area.id}>
                    <button
                      type="button"
                      className="help-videos-area-card"
                      onClick={() => selectArea(area.id)}
                    >
                      <span className="help-videos-area-card-title">{area.title}</span>
                      <span className="help-videos-area-card-desc">{area.description}</span>
                      <span className="help-videos-area-card-meta">
                        {area.videos.length} video{area.videos.length === 1 ? '' : 's'}
                        {readyCount > 0
                          ? ` · ${readyCount} ready to watch`
                          : ' · written steps available now'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : (
        <section className="help-videos-area" aria-labelledby="help-videos-area-heading">
          <div className="help-videos-area-toolbar">
            <button
              type="button"
              className="help-videos-back-topics"
              onClick={() => selectArea('')}
            >
              ← All topics
            </button>
            {selectedArea.relatedPath && (
              <Link to={selectedArea.relatedPath} className="help-videos-open-feature">
                Open {selectedArea.title}
              </Link>
            )}
          </div>

          <h2 id="help-videos-area-heading" className="help-videos-section-title">
            {selectedArea.title}
          </h2>
          <p className="help-videos-area-blurb">{selectedArea.description}</p>

          <ul className="help-videos-video-list">
            {selectedArea.videos.map((video) => {
              const isOpen = openVideoId === video.id;
              const panelId = `help-video-panel-${video.id}`;
              return (
                <li key={video.id} className="help-videos-video-item">
                  <button
                    type="button"
                    className="help-videos-video-toggle"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleVideo(video.id)}
                  >
                    <span className="help-videos-video-toggle-chevron" aria-hidden>
                      {isOpen ? '▼' : '▶'}
                    </span>
                    <span className="help-videos-video-toggle-text">
                      <strong>{video.title}</strong>
                      {video.duration && (
                        <span className="help-videos-video-duration">{video.duration}</span>
                      )}
                      {!hasPlayableVideo(video) && (
                        <span className="help-videos-video-badge">Steps ready</span>
                      )}
                    </span>
                  </button>
                  {isOpen && (
                    <div id={panelId} className="help-videos-video-panel">
                      <HelpVideoEmbed video={video} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
};

export default HelpVideosPage;
